import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Canvas, useThree } from "@react-three/fiber";
import { Sphere } from "@react-three/drei";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import * as THREE from "three";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const HomeContainer = styled.div`
  width: 100%;
  height: 100vh;
  position: relative;
`;

const HeroSection = styled.div`
  width: 100%;
  height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeroContent = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 1;
  color: white;
`;

const Title = styled(motion.h1)`
  font-size: 4rem;
  user-select: none;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #ffffff, #888888);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.5rem;
  user-select: none;
  color: #888888;
`;

const CanvasContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const AnimatedSphere = () => {
  const meshRef = useRef<THREE.Mesh>(null);
  const materialRef = useRef<THREE.ShaderMaterial>(null);
  const timeRef = useRef(0);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    // Track mouse position
    const handleMouseMove = (event: MouseEvent) => {
      // Convert to normalized device coordinates properly
      mouseRef.current.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouseRef.current.y = -((event.clientY / window.innerHeight) * 2 - 1);
    };

    window.addEventListener("mousemove", handleMouseMove);

    if (meshRef.current && materialRef.current) {
      // Continuous rotation animation
      gsap.to(meshRef.current.rotation, {
        y: Math.PI * 2,
        duration: 20,
        repeat: -1,
        ease: "none",
      });

      // Scroll-based animations
      gsap.fromTo(
        meshRef.current.rotation,
        { x: 0, z: 0 },
        {
          x: Math.PI * 4,
          z: Math.PI * 2,
          scrollTrigger: {
            trigger: "#home",
            start: "top top",
            end: "bottom top",
            scrub: 1,
            toggleActions: "play none none reverse",
          },
        }
      );

      // Scale animation based on scroll
      gsap.fromTo(
        meshRef.current.scale,
        { x: 1, y: 1, z: 1 },
        {
          x: 0.3,
          y: 0.3,
          z: 0.3,
          scrollTrigger: {
            trigger: "#home",
            start: "top top",
            end: "bottom top",
            scrub: 1,
          },
        }
      );

      // Position animation - move the sphere as user scrolls
      gsap.fromTo(
        meshRef.current.position,
        { x: 0, y: 0 },
        {
          x: 3,
          y: -2,
          scrollTrigger: {
            trigger: "#home",
            start: "top top",
            end: "bottom top",
            scrub: 1,
            toggleActions: "play none none reverse",
          },
        }
      );

      // Shader animation function
      const animateShader = () => {
        if (materialRef.current) {
          timeRef.current += 0.02;

          // Update shader uniforms
          materialRef.current.uniforms.u_time.value = timeRef.current;
          materialRef.current.uniforms.u_mouse.value.set(
            mouseRef.current.x,
            mouseRef.current.y
          );
        }
        requestAnimationFrame(animateShader);
      };

      // Start shader animation
      animateShader();
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  // Custom vertex shader
  const vertexShader = `
    varying vec3 vPosition;
    varying vec3 vNormal;
    
    void main() {
      vPosition = position;
      vNormal = normal;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `;

  // Custom fragment shader
  const fragmentShader = `
    uniform float u_time;
    uniform vec2 u_mouse;
    varying vec3 vPosition;
    varying vec3 vNormal;
    
    vec3 hsv2rgb(vec3 c) {
      vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
      vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
      return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
    }
    
    void main() {
      // Use a simpler approach - compare mouse position to sphere's front-facing surface
      vec2 sphereScreenPos = normalize(vPosition).xy;
      
      // Calculate distance from mouse position
      float mouseDistance = distance(sphereScreenPos, u_mouse * 3.0);
      
      // Create influence area around the mouse
      float influence = 1.0 - smoothstep(0.0, 0.8, mouseDistance);
      
      // Generate rainbow colors based on position and time
      float hue = atan(vPosition.y, vPosition.x) * 0.159 + u_time * 0.1;
      hue += length(vPosition.xy) * 0.3;
      vec3 rainbowColor = hsv2rgb(vec3(hue, 0.9, 1.0));

      // Base gray color
      vec3 baseColor = vec3(0.5, 0.5, 0.5);
      
      // Mix between white and rainbow based on mouse influence
      vec3 finalColor = mix(baseColor, rainbowColor, influence);
      
      gl_FragColor = vec4(finalColor, 0.8);
    }
  `;

  const uniforms = {
    u_time: { value: 0 },
    u_mouse: { value: new THREE.Vector2(0, 0) },
  };

  return (
    <Sphere args={[2, 64, 64]} ref={meshRef}>
      <shaderMaterial
        ref={materialRef}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        wireframe
        transparent
        opacity={0.8}
      />
    </Sphere>
  );
};

const AnimatedCamera = () => {
  const { camera } = useThree();

  useEffect(() => {
    // Set initial camera position - zoomed in close to the sphere
    camera.position.set(0, 0, 2);

    // Animate camera zoom out and slight movement based on scroll
    gsap.fromTo(
      camera.position,
      { z: 2, x: 0, y: 0 },
      {
        z: 8,
        x: 1,
        y: 0.5,
        scrollTrigger: {
          trigger: "#home",
          start: "top top",
          end: "bottom top",
          scrub: 1,
          toggleActions: "play none none reverse",
          onUpdate: () => {
            camera.updateProjectionMatrix();
          },
        },
      }
    );

    // Add a subtle camera rotation for more dynamic feel
    gsap.fromTo(
      camera.rotation,
      { y: 0, x: 0 },
      {
        y: 0.1,
        x: -0.05,
        scrollTrigger: {
          trigger: "#home",
          start: "top top",
          end: "bottom top",
          scrub: 2,
          toggleActions: "play none none reverse",
          onUpdate: () => {
            camera.updateProjectionMatrix();
          },
        },
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [camera]);

  return null;
};

const Home = () => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    // Ensure page starts at the top on load
    window.scrollTo(0, 0);

    // Scroll-based text animations
    if (titleRef.current) {
      gsap.fromTo(
        titleRef.current,
        { y: 0, opacity: 1 },
        {
          y: -100,
          opacity: 0.3,
          scrollTrigger: {
            trigger: "#home",
            start: "top top",
            end: "bottom top",
            scrub: 1,
            toggleActions: "play none none reverse",
          },
        }
      );
    }

    if (subtitleRef.current) {
      gsap.fromTo(
        subtitleRef.current,
        { y: 0, opacity: 1 },
        {
          y: -80,
          opacity: 0.1,
          scrollTrigger: {
            trigger: "#home",
            start: "top top",
            end: "bottom top",
            scrub: 1,
            toggleActions: "play none none reverse",
          },
        }
      );
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <HomeContainer>
      <HeroSection>
        <CanvasContainer>
          <Canvas camera={{ position: [0, 0, 2] }}>
            <ambientLight intensity={0.5} />
            <pointLight position={[10, 10, 10]} />
            <spotLight position={[-10, -10, -10]} angle={0.15} penumbra={1} />
            <AnimatedCamera />
            <AnimatedSphere />
          </Canvas>
        </CanvasContainer>
        <HeroContent>
          <Title
            ref={titleRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Creative Developer
          </Title>
          <Subtitle
            ref={subtitleRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Crafting digital experiences with code and creativity
          </Subtitle>
        </HeroContent>
      </HeroSection>
    </HomeContainer>
  );
};

export default Home;
